import './BufferOptInCell.scss';

import type { SwitchChangeEventHandler } from 'antd/lib/switch';
import { User } from 'mission-control-common-components';
import React, { memo, useState } from 'react';

import type { LineItemSsp } from '../../../../../apis/graphql';
import ModalPopup from '../../../../../common/ModalPopup';
import ToggleSwitch from '../../../../../common/ToggleSwitch';
import Tooltip from '../../../../../common/Tooltip';
import { onLineItemFailureUpdate } from '../../../../../common/utils';
import type { BillableThirdParty, DisplayEnum, PacingType } from '../../../../../configs';
import type { PermissionsNames } from '../../../../../constants';
import type { Nullable } from '../../../../../models';
import bem from '../../../../../utils/bem';
import { getDraxNullBillableThirdPartyBufferTooltip, shouldDisableBufferOptIn } from '../../../../../utils/lineItem';
import { useUpdateLineItem } from '../../hooks';

type Props = Readonly<{
  automateBuffer: Nullable<boolean>;
  lineItemId: string;
  pacingType: DisplayEnum<PacingType>;
  ssp?: Nullable<LineItemSsp>;
  billableThirdParty?: Nullable<DisplayEnum<BillableThirdParty>>;
}>;

const [block] = bem('buffer-opt-in-cell');

const CONFIRMATION_MESSAGE = 'Are you sure you want to change the Buffer Opt-In setting?';
const CANCEL_BUTTON_LABEL = 'Cancel';
const CONFIRMATION_BUTTON_LABEL = 'Confirm';

function BufferOptInCell({
  automateBuffer,
  lineItemId,
  pacingType,
  ssp,
  billableThirdParty,
}: Props): React.JSX.Element {
  const permissions = User.getPermissions<PermissionsNames>();
  const { updateLineItemField, loading } = useUpdateLineItem(lineItemId, 'automateBuffer', onLineItemFailureUpdate);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean>(false);

  const isOn = automateBuffer ?? false;
  const isDisabledByBusinessRules = shouldDisableBufferOptIn({ pacingType, ssp, billableThirdParty });
  const draxTooltipMessage = getDraxNullBillableThirdPartyBufferTooltip({ ssp, billableThirdParty });

  const handleChange: SwitchChangeEventHandler = (checked, event) => {
    event.stopPropagation();
    setPendingValue(checked);
    setIsModalVisible(true);
  };

  const handleConfirm = (): void => {
    updateLineItemField(pendingValue);
    setIsModalVisible(false);
  };

  const handleCancel = (): void => {
    setIsModalVisible(false);
  };

  return (
    <div className={block()}>
      <Tooltip id={`buffer-opt-in-cell-${lineItemId}`} message={draxTooltipMessage ?? ''} disable={!draxTooltipMessage}>
        <ToggleSwitch
          isOn={isOn}
          disabled={isDisabledByBusinessRules || !permissions?.updateLineItem || loading}
          onChange={handleChange}
        />
      </Tooltip>
      <ModalPopup
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={handleConfirm}
        modalProps={{
          title: CONFIRMATION_MESSAGE,
          cancelText: CANCEL_BUTTON_LABEL,
          okText: CONFIRMATION_BUTTON_LABEL,
          okButtonProps: {
            disabled: loading,
            loading: loading,
          },
        }}
      />
    </div>
  );
}

export default memo(BufferOptInCell);
